import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import DOMPurify from "dompurify";
import { $getRoot, $insertNodes, type LexicalEditor } from "lexical";

import { INLINE_FORMATS } from "../configs/formats";

interface TagRewrite {
  readonly tag: string;
  readonly className: string;
}

const INLINE_TAG_REWRITES: Readonly<Record<string, TagRewrite>> = {
  ...Object.fromEntries(INLINE_FORMATS.map(({ lexicalTag, tag, className }) => [lexicalTag, { tag, className }])),
  p: { tag: "p", className: "paragraph" },
};

const renameElement = (element: Element, tag: string): Element => {
  if (element.tagName.toLowerCase() === tag) return element;
  const next = element.ownerDocument.createElement(tag);
  for (const attr of Array.from(element.attributes)) next.setAttribute(attr.name, attr.value);
  while (element.firstChild) next.appendChild(element.firstChild);
  element.replaceWith(next);
  return next;
};

const normalizeCodeBlocks = (doc: Document): void => {
  for (const pre of Array.from(doc.querySelectorAll("pre"))) {
    pre.classList.add("code-area");
    pre.removeAttribute("style");
    const code = pre.querySelector(":scope > code");
    if (code) code.removeAttribute("style");
  }
};

const rewriteInlineTags = (doc: Document): void => {
  for (const element of Array.from(doc.body.querySelectorAll<HTMLElement>("*"))) {
    if (element.closest("pre")) continue;
    const rewrite = INLINE_TAG_REWRITES[element.tagName.toLowerCase()];
    if (!rewrite) continue;
    element.removeAttribute("style");
    const renamed = renameElement(element, rewrite.tag);
    renamed.classList.add(rewrite.className);
  }
};

const collapseRedundantNesting = (doc: Document): void => {
  let changed = true;
  while (changed) {
    changed = false;
    for (const element of Array.from(doc.body.querySelectorAll<HTMLElement>("*"))) {
      if (element.childNodes.length !== 1) continue;
      const child = element.firstElementChild;
      if (!child || child !== element.firstChild) continue;
      if (child.tagName !== element.tagName) continue;
      for (const cls of Array.from(child.classList)) element.classList.add(cls);
      while (child.firstChild) element.appendChild(child.firstChild);
      child.remove();
      changed = true;
    }
  }
};

const stripRteClasses = (doc: Document): void => {
  for (const element of Array.from(doc.body.querySelectorAll<HTMLElement>("[class]"))) {
    for (const cls of Array.from(element.classList)) {
      if (cls.startsWith("rte-")) element.classList.remove(cls);
    }
    if (!element.classList.length) element.removeAttribute("class");
  }
};

const hardenAnchors = (doc: Document): void => {
  for (const anchor of Array.from(doc.body.querySelectorAll<HTMLAnchorElement>("a[href]"))) {
    anchor.setAttribute("target", "_blank");
    const rel = (anchor.getAttribute("rel") ?? "").split(/\s+/).filter(Boolean);
    for (const token of ["noopener", "noreferrer"]) {
      if (!rel.includes(token)) rel.push(token);
    }
    anchor.setAttribute("rel", rel.join(" "));
  }
};

const relocateInlineEdgeWhitespace = (doc: Document): void => {
  for (const element of Array.from(doc.body.querySelectorAll<HTMLElement>("*"))) {
    if (element.closest("pre")) continue;
    const tag = element.tagName.toLowerCase();
    if (!(tag in INLINE_TAG_REWRITES) || tag === "p") continue;
    pushEdgeWhitespaceOutside(element);
  }
};

const normalizeSerializedHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  normalizeCodeBlocks(doc);
  rewriteInlineTags(doc);
  collapseRedundantNesting(doc);
  stripRteClasses(doc);
  hardenAnchors(doc);
  relocateInlineEdgeWhitespace(doc);
  return doc.body.innerHTML;
};

const LEADING_WHITESPACE = /^(\s+)/;
const TRAILING_WHITESPACE = /(\s+)$/;

const findFirstTextNode = (root: Node): Text | null => {
  if (root.nodeType === Node.TEXT_NODE) return root as Text;
  for (const child of Array.from(root.childNodes)) {
    const found = findFirstTextNode(child);
    if (found) return found;
  }
  return null;
};

const findLastTextNode = (root: Node): Text | null => {
  if (root.nodeType === Node.TEXT_NODE) return root as Text;
  const children = Array.from(root.childNodes);
  for (let i = children.length - 1; i >= 0; i -= 1) {
    const found = findLastTextNode(children[i]);
    if (found) return found;
  }
  return null;
};

const pushEdgeWhitespaceOutside = (element: Element): void => {
  const doc = element.ownerDocument;
  const head = findFirstTextNode(element);
  if (head) {
    const match = LEADING_WHITESPACE.exec(head.data);
    if (match) {
      head.data = head.data.slice(match[1].length);
      element.parentNode?.insertBefore(doc.createTextNode(match[1]), element);
    }
  }
  const tail = findLastTextNode(element);
  if (tail) {
    const match = TRAILING_WHITESPACE.exec(tail.data);
    if (match) {
      tail.data = tail.data.slice(0, tail.data.length - match[1].length);
      element.parentNode?.insertBefore(doc.createTextNode(match[1]), element.nextSibling);
    }
  }
  if (element.textContent === "" && element.childNodes.length === 0) element.remove();
};

export const serializeHtml = (editor: LexicalEditor): string =>
  DOMPurify.sanitize(normalizeSerializedHtml($generateHtmlFromNodes(editor, null)));

export const hydrateHtml = (editor: LexicalEditor, html: string): void => {
  $getRoot().clear();
  const parsed = new DOMParser().parseFromString(DOMPurify.sanitize(html), "text/html");
  $insertNodes($generateNodesFromDOM(editor, parsed));
};
